import {ChatRepository} from "@buildwithflux/repositories";
import {Logger, Records} from "@buildwithflux/shared";
import {keys, omitBy} from "lodash";

import {CopilotChatStoreState} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/useCopilotChatStore";

export function setCurrentDocumentUid(
    set: (fn: (state: CopilotChatStoreState) => void) => void,
    get: () => CopilotChatStoreState,
    chatRepository: ChatRepository,
    logger: Logger,
) {
    return (documentUid: string) => {
        const existingSubscription = get().threadsSubscription;
        if (existingSubscription) {
            existingSubscription();
        }

        // Use a regular `set` outside of the draft scope to safely update the store
        const unsubscribe = chatRepository.subscribeToAllThreads(
            documentUid,
            (addedOrChangedThreads) => {
                get().handleBackendUpdatedThreads(addedOrChangedThreads);
            },
            (removedThreads) => {
                // Capture whether we had any threads before this update.
                // We only want to auto-select a thread when we're receiving the first threads
                // for this document (initial load), not on subsequent updates (e.g., during
                // streaming), to avoid overriding the user's choice when they intentionally
                // navigate to a new thread.
                const hadThreadsBefore = !Records.isEmpty(get().threads);

                set((state) => {
                    const uidsToRemove = keys(removedThreads);

                    return {
                        ...state,
                        threadsLoaded: true,
                        threads: omitBy(state.threads, (value) => uidsToRemove.includes(value.uid)),
                    };
                });

                const {currentThreadUid, pendingThreadUidFromUrl} = get();
                // Only auto-select a thread on initial load (when we had no threads before).
                // This prevents overriding the user's choice when they intentionally navigate
                // to a new thread while another thread is streaming.
                if (!currentThreadUid && !pendingThreadUidFromUrl && !hadThreadsBefore) {
                    get().setCurrentThreadToLatest();
                }
            },
            (error) => {
                logger.error("Error subscribing to chat threads", error);
            },
        );

        void get().fetchKnowledgeBaseSuggestions();

        set((draft) => {
            if (draft.currentDocumentUid !== documentUid) {
                delete draft.currentThreadUid;
                draft.isShowingWelcomeMessage = true;
                draft.threads = {};
            }

            draft.currentDocumentUid = documentUid;
            draft.threadsSubscription = unsubscribe;
        });
    };
}
